import React from 'react';
import { Calendar, Plus, Edit2, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { ProjectScoping } from '../../types';
import { SectionHeader } from './SectionHeader';

interface TimingSectionProps {
    timing: ProjectScoping['timing'];
    isDark: boolean;
    canEdit: boolean;
    onEdit: () => void;
    onAddMilestone: () => void;
    onEditMilestone: (idx: number) => void;
    onDeleteMilestone: (idx: number) => void;
    getStatusColor: (status: string) => string;
    getStatusIcon: (status: string) => React.ReactNode;
}

export const TimingSection: React.FC<TimingSectionProps> = ({ timing, isDark, canEdit, onEdit, onAddMilestone, onEditMilestone, onDeleteMilestone, getStatusColor, getStatusIcon }) => (
    <div className={clsx('p-4 rounded-lg border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        <SectionHeader icon={Calendar} colorClass="text-blue-500" title="2. Timing (Schedule & Milestones)" onEdit={onEdit} canEdit={canEdit} isDark={isDark} />
        <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
                <div className="text-sm font-medium text-slate-500 mb-1">Kickoff Date</div>
                <div className={clsx('text-sm font-mono', isDark ? 'text-slate-300' : 'text-slate-700')}>{timing.kickoffDate.toLocaleDateString()}</div>
            </div>
            <div>
                <div className="text-sm font-medium text-slate-500 mb-1">Target Completion</div>
                <div className={clsx('text-sm font-mono', isDark ? 'text-slate-300' : 'text-slate-700')}>{timing.targetCompletionDate.toLocaleDateString()}</div>
            </div>
        </div>
        <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-slate-500">Milestones</div>
            {canEdit && (
                <button onClick={onAddMilestone} className="text-xs text-primary-500 hover:text-primary-400 flex items-center gap-1">
                    <Plus size={14} /> Add Milestone
                </button>
            )}
        </div>
        <div className="space-y-2">
            {timing.milestones.map((milestone, idx) => (
                <div key={idx} className={clsx('flex items-center justify-between p-2 rounded border', isDark ? 'border-slate-700' : 'border-slate-200')}>
                    <div className="flex items-center gap-3">
                        <span className={clsx('px-2 py-0.5 rounded text-xs flex items-center gap-1', getStatusColor(milestone.status))}>
                            {getStatusIcon(milestone.status)}
                            {milestone.status}
                        </span>
                        <span className={clsx('text-sm', isDark ? 'text-white' : 'text-slate-800')}>{milestone.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-500">{milestone.date.toLocaleDateString()}</span>
                        {canEdit && (
                            <>
                                <button onClick={() => onEditMilestone(idx)} className="p-1 hover:bg-slate-600 rounded"><Edit2 size={14} className="text-slate-400" /></button>
                                <button onClick={() => onDeleteMilestone(idx)} className="p-1 hover:bg-red-700 rounded"><Trash2 size={14} className="text-red-400" /></button>
                            </>
                        )}
                    </div>
                </div>
            ))}
        </div>
    </div>
);
